import { NetworkStatus, useApolloClient, useQuery } from "@apollo/client";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { CursorPagination } from "../components/CursorPagination";
import { RequirePermission } from "../components/RequirePermission";
import { PhoneNumberLink } from "../components/telephony/PhoneNumberLink";
import { TELEPHONY_RESOURCE } from "../constants/resources";
import {
  CallKpiRange,
  CallListFilter,
  CustomerServiceProviderCallsDocument,
  type CustomerServiceProviderCallsQuery,
  type CustomerServiceProviderCallsQueryVariables,
} from "../generated/graphql";
import { CRM_COLORS, CRM_ROUTE, formatCallDuration, formatCrmCount, formatCrmDateTime, isE164Number } from "../lib/crm";

const PAGE_SIZE = 50;

type CallList = NonNullable<CustomerServiceProviderCallsQuery["customerServiceProviderCalls"]>;
type CallPagination = {
  response?: CustomerServiceProviderCallsQuery;
  page: number;
  cursors: (string | null)[];
  terminalPage: number | null;
};

const isRange = (value: string | null): value is CallKpiRange =>
  Object.values<string>(CallKpiRange).includes(value ?? "");
const isFilter = (value: string | null): value is CallListFilter =>
  Object.values<string>(CallListFilter).includes(value ?? "");

const KNOWN_DIRECTIONS = ["inbound", "outbound", "internal"];
const KNOWN_OUTCOMES = ["answered", "abandoned", "missed", "not_routed", "not_answered"];

const CallRows = ({ rows }: { rows: CallList["rows"] }) => {
  const translate = useTranslate();
  const none = translate("crm.calls.none");
  const text = (group: string, known: string[], value: string | null | undefined): string =>
    !value ? none : known.includes(value) ? translate(`crm.calls.${group}.${value}`) : value;
  const duration = (seconds: number | null | undefined): string =>
    seconds === null || seconds === undefined ? none : formatCallDuration(seconds);
  return (
    <Table size="small" aria-label={translate("crm.calls.overline")}>
      <TableHead>
        <TableRow>
          {["time", "direction", "number", "agent", "outcome", "wait", "handle"].map(column => (
            <TableCell key={column} align={column === "wait" || column === "handle" ? "right" : "left"}>
              {translate(`crm.calls.columns.${column}`)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => (
          <TableRow key={row.id} data-testid="crm-call-row">
            <TableCell>{formatCrmDateTime(row.startedAt)}</TableCell>
            <TableCell>{text("directions", KNOWN_DIRECTIONS, row.direction)}</TableCell>
            <TableCell>
              {!row.customerNumber ? (
                none
              ) : isE164Number(row.customerNumber) ? (
                <PhoneNumberLink number={row.customerNumber} />
              ) : (
                row.customerNumber
              )}
            </TableCell>
            <TableCell>{row.agentName || none}</TableCell>
            <TableCell>{text("outcomes", KNOWN_OUTCOMES, row.outcome)}</TableCell>
            <TableCell align="right">{duration(row.waitSeconds)}</TableCell>
            <TableCell align="right">{duration(row.handleSeconds)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const CrmCalls = ({
  range,
  filter,
  agentKey,
}: {
  range: CallKpiRange;
  filter: CallListFilter;
  agentKey: string | null;
}) => {
  const translate = useTranslate();
  const client = useApolloClient();
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [pagination, setPagination] = useState<CallPagination>({ page: 1, cursors: [null], terminalPage: null });
  const { page, cursors, terminalPage } = pagination;
  const inFlight = useRef(false);
  const requestGeneration = useRef(0);
  const baseRefetching = useRef(false);
  const variables: CustomerServiceProviderCallsQueryVariables = { range, filter, agentKey, first: PAGE_SIZE };
  const {
    data: baseData,
    loading,
    error,
    networkStatus,
  } = useQuery<CustomerServiceProviderCallsQuery, CustomerServiceProviderCallsQueryVariables>(
    CustomerServiceProviderCallsDocument,
    {
      variables,
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    }
  );
  if (networkStatus === NetworkStatus.refetch && !baseRefetching.current) {
    baseRefetching.current = true;
    requestGeneration.current += 1;
    inFlight.current = false;
    setLoadingMore(false);
  } else if (networkStatus !== NetworkStatus.refetch && baseRefetching.current) {
    baseRefetching.current = false;
    if (networkStatus === NetworkStatus.ready && !error && baseData) {
      setPagination({ page: 1, cursors: [null], terminalPage: null });
      setMoreFailed(false);
    }
  }
  const data = pagination.response ?? baseData;
  const list = data?.customerServiceProviderCalls;

  // A page that arrives after the range, filter or agent changed belongs to rows no longer shown.
  const variablesKey = JSON.stringify(variables);
  const shownKey = useRef<string | null>(variablesKey);
  shownKey.current = variablesKey;
  useEffect(() => setMoreFailed(false), [variablesKey]);
  useEffect(() => {
    shownKey.current = variablesKey;
    return () => {
      shownKey.current = null;
    };
  }, [variablesKey]);
  const hasNextPage = Boolean(list?.hasMore && terminalPage !== page);

  const changePage = async (next: number) => {
    if (inFlight.current || loading || !list || next === page || next < 1) return;
    const newPage = next === page + 1 && next === cursors.length + 1;
    const after = newPage ? list.endCursor : cursors[next - 1];
    if (after === undefined || (newPage && (!hasNextPage || !after))) return;
    const requested = variablesKey;
    const generation = requestGeneration.current;
    inFlight.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    const requestPage = (cursor: string | null) =>
      client.query<CustomerServiceProviderCallsQuery, CustomerServiceProviderCallsQueryVariables>({
        query: CustomerServiceProviderCallsDocument,
        variables: { ...variables, after: cursor },
        fetchPolicy: "no-cache",
      });
    try {
      const result = await requestPage(after);
      if (requested !== shownKey.current || generation !== requestGeneration.current) return;
      const nextList = result.data.customerServiceProviderCalls;
      if (!nextList) return;
      if (next > page && nextList.rows.length === 0) {
        setPagination(current => ({ ...current, terminalPage: page }));
        return;
      }
      if (next > 1 && nextList.rows.length === 0) {
        const first = await requestPage(null);
        if (requested !== shownKey.current || generation !== requestGeneration.current) return;
        if (!first.data.customerServiceProviderCalls) return;
        setPagination({ response: first.data, page: 1, cursors: [null], terminalPage: null });
        return;
      }
      setPagination({
        response: result.data,
        page: next,
        cursors: newPage ? [...cursors, after] : cursors,
        terminalPage,
      });
    } catch {
      if (requested === shownKey.current && generation === requestGeneration.current) setMoreFailed(true);
    } finally {
      if (requested === shownKey.current && generation === requestGeneration.current) {
        inFlight.current = false;
        setLoadingMore(false);
      }
    }
  };

  const title = translate(`crm.calls.titles.${filter}`);
  const agentName = agentKey ? list?.rows[0]?.agentName : null;

  return (
    <Box data-testid="crm-calls">
      <Title title={title} />
      <CrmPageHeader
        icon={
          <Box
            aria-hidden
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "4px",
              backgroundColor: CRM_COLORS.brand,
              color: CRM_COLORS.iconForeground,
            }}
          >
            <HeadsetMicOutlinedIcon />
          </Box>
        }
        overline={translate("crm.calls.overline")}
        title={title}
        meta={
          list && (
            <>
              {translate(`crm.telephony.ranges.${range}`)} •{" "}
              {translate("crm.calls.count", { smart_count: list.totalCount, total: formatCrmCount(list.totalCount) })}
              {agentName && ` • ${translate("crm.calls.agent", { name: agentName })}`}
            </>
          )
        }
        actions={
          <Button
            component={RouterLink}
            to={CRM_ROUTE}
            startIcon={<ArrowBackOutlinedIcon />}
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
          >
            {translate("crm.calls.back")}
          </Button>
        }
      />
      {(error || moreFailed) && <Alert severity="error">{translate("crm.loadError")}</Alert>}
      {loading && !data && <CircularProgress aria-label={translate("crm.loading")} />}
      {data && !list && <Alert severity="warning">{translate("crm.calls.unavailable")}</Alert>}
      {list && (
        <CrmCard flush>
          {list.rows.length === 0 ? (
            <Typography sx={{ p: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
              {translate("crm.calls.empty")}
            </Typography>
          ) : (
            <CallRows rows={list.rows} />
          )}
          <CursorPagination
            page={page}
            perPage={PAGE_SIZE}
            itemCount={list.rows.length}
            hasNextPage={hasNextPage}
            isPending={loading || loadingMore}
            resource={TELEPHONY_RESOURCE}
            onPageChange={next => void changePage(next)}
          />
        </CrmCard>
      )}
    </Box>
  );
};

// The calls behind one KPI of the Service-Übersicht, named by the range, filter and agent of the address.
const CrmCallsRoute = () => {
  const translate = useTranslate();
  const [params] = useSearchParams();
  const range = params.get("range");
  const filter = params.get("filter");
  if (!isRange(range) || !isFilter(filter)) {
    return (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" component={RouterLink} size="small" to={CRM_ROUTE}>
            {translate("crm.calls.back")}
          </Button>
        }
      >
        {translate("crm.calls.invalid")}
      </Alert>
    );
  }
  return (
    <CrmCalls
      key={`${range}|${filter}|${params.get("agent") ?? ""}`}
      range={range}
      filter={filter}
      agentKey={params.get("agent")}
    />
  );
};

export const CrmCallsPage = () => (
  <RequirePermission permission={TELEPHONY_RESOURCE}>
    <CrmCallsRoute />
  </RequirePermission>
);
